import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import {
  PiWarningCircleLight,
  PiBellLight,
  PiBookOpenLight,
  PiBriefcaseLight,
  PiCaretRightLight,
  PiCommandLight,
  PiSignatureLight,
  PiMagnifyingGlassLight,
  PiTargetLight,
  PiUsersLight,
  PiChartBarLight,
  PiCircleNotchLight,
  PiChartLineUpLight,
  PiFunnelLight,
  PiPulseLight,
  PiCheckCircleLight,
  PiLightbulbLight,
  PiArrowUpRightLight,
  PiCalendarCheckLight
} from 'react-icons/pi';
import { dashboardAPI, employerAPI } from '../../services/api';
import DashboardGuideModal from '../../components/dashboard/DashboardGuideModal';
import { useUIStore } from '../../store';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Tooltip,
  Legend
);

const palette = {
  ink: '#0b1f3a',
  blue: '#026feb',
  sky: '#0ea4f9',
  cyan: '#23cbfc',
  pale: '#95e5fc',
  soft: '#e9f9ff',
  slate: '#64748b'
};

const timeframeOptions = [
  { label: '30D', value: '30d' },
  { label: '90D', value: '90d' },
  { label: '1Y', value: '1y' }
];

const formatNumber = (value) => (
  new Intl.NumberFormat('en-US', { notation: Math.abs(Number(value) || 0) >= 10000 ? 'compact' : 'standard' }).format(Number(value) || 0)
);

const formatPercent = (value) => `${Math.round(Number(value) || 0)}%`;

const formatMoney = (value) => {
  const amount = Number(value) || 0;
  if (!amount) return 'No offers';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(amount);
};

const formatDateTime = (value) => {
  if (!value) return 'Not scheduled';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(new Date(value));
};

const relativeTime = (value) => {
  if (!value) return 'No timestamp';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'No timestamp';

  const diff = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diff < minute) return 'Just now';
  if (diff < hour) return `${Math.floor(diff / minute)}m ago`;
  if (diff < day) return `${Math.floor(diff / hour)}h ago`;
  return `${Math.floor(diff / day)}d ago`;
};

const labelize = (value) => (
  String(value || 'Unknown')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase())
);

const getNotificationDestination = (notification) => {
  const entityType = String(notification?.relatedEntityType || '').toLowerCase();
  const type = String(notification?.type || '').toLowerCase();
  const data = notification?.data || {};

  if (entityType.includes('job') || type.includes('job')) {
    const jobId = data.jobId || notification.relatedEntityId;
    return jobId ? `/jobs/${jobId}` : '/jobs';
  }
  if (entityType.includes('candidate') || entityType.includes('match') || type.includes('match')) {
    const candidateId = data.candidateId || (entityType.includes('candidate') ? notification.relatedEntityId : null);
    return candidateId ? `/candidates/${candidateId}` : '/candidates';
  }
  if (entityType.includes('interview') || type.includes('interview')) return '/interviews';
  if (entityType.includes('offer') || type.includes('offer')) return '/offers';
  if (entityType.includes('assessment') || type.includes('assessment')) return '/assessments';
  if (entityType.includes('profile') || type.includes('profile')) return '/profile';
  if (entityType.includes('kyc') || type.includes('verification')) return '/kyc-verification';
  return null;
};

const normalizeSkill = (skill) => {
  if (!skill) return null;
  if (typeof skill === 'string') return skill;
  return skill.name || skill.skill || skill.label || null;
};



const getTrendCopy = (trend) => {
  if (!trend) return 'No period data';
  const change = Number(trend.change) || 0;
  if (change === 0) return 'Flat this period';
  return `${change > 0 ? '+' : ''}${change}% vs prior`;
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      labels: {
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        color: '#475569',
        font: { family: 'Inter', size: 11, weight: '500' }
      }
    },
    tooltip: {
      backgroundColor: '#0b1f3a',
      borderColor: 'rgba(255,255,255,0.12)',
      borderWidth: 1,
      padding: 10,
      titleFont: { family: 'Inter', size: 12, weight: '600' },
      bodyFont: { family: 'Inter', size: 12 },
      displayColors: true
    }
  },
  scales: {
    x: {
      grid: { display: false },
      ticks: { color: '#94a3b8', font: { family: 'Inter', size: 11 } },
      border: { display: false }
    },
    y: {
      beginAtZero: true,
      grid: { color: 'rgba(148,163,184,0.16)' },
      ticks: { color: '#94a3b8', font: { family: 'Inter', size: 11 }, precision: 0 },
      border: { display: false }
    }
  }
};

const doughnutOptions = {
  responsive: true,
  maintainAspectRatio: false,
  cutout: '70%',
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        boxWidth: 8,
        boxHeight: 8,
        usePointStyle: true,
        color: '#475569',
        font: { family: 'Inter', size: 11, weight: '500' },
        padding: 14
      }
    },
    tooltip: chartOptions.plugins.tooltip
  }
};

const EmptyState = ({ label = 'No backend data yet' }) => (
  <div className="flex h-full min-h-[180px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/70 text-sm text-slate-500">
    {label}
  </div>
);

const SectionHeader = ({ icon: Icon, title, subtitle, action }) => (
  <div className="mb-4 flex items-start justify-between gap-4">
    <div className="flex min-w-0 items-start gap-3">
      {Icon && (
        <span className="mt-0.5 flex shrink-0 items-center justify-center text-slate-700">
          <Icon size={24} />
        </span>
      )}
      <div className="min-w-0">
        <h2 className="font-display text-[17px] font-normal leading-tight text-[#0b1f3a]">{title}</h2>
        {subtitle && <p className="mt-1 text-xs leading-5 text-slate-500">{subtitle}</p>}
      </div>
    </div>
    {action}
  </div>
);

const Panel = ({ icon, title, subtitle, children, className = '', action }) => (
  <section className={`min-w-0 rounded-lg border border-slate-200/80 bg-white shadow-[0_14px_36px_rgba(15,23,42,0.045)] ${className}`}>
    <div className="p-4 sm:p-5">
      <SectionHeader icon={icon} title={title} subtitle={subtitle} action={action} />
      {children}
    </div>
  </section>
);

const CompanyKnowledgeBanner = ({ progress, onClick, highlighted, bannerRef }) => {
  const completion = Math.max(0, Math.min(100, Number(progress?.completionPercentage) || 0));
  const answered = Number(progress?.answeredQuestions) || 0;
  const total = Number(progress?.totalQuestions) || 0;

  return (
    <button
      ref={bannerRef}
      type="button"
      onClick={onClick}
      className={`group relative block w-full overflow-hidden rounded-lg border bg-white text-left transition-all duration-700 ${
        highlighted
          ? 'z-[70] scale-[1.01] border-sky-300 shadow-[0_20px_50px_rgba(14,164,249,0.18)] ring-4 ring-sky-400/25'
          : 'border-sky-100 shadow-[0_16px_42px_rgba(15,23,42,0.06)] hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-[0_22px_54px_rgba(15,23,42,0.09)]'
      }`}
    >
      <div className="grid gap-4 p-4 sm:p-5 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-center">
        <div className="flex min-w-0 gap-4">
          <span className="flex shrink-0 items-center justify-center text-[#0b1f3a] mt-1">
            <PiBookOpenLight size={26} />
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-[#eef9ff] px-2 py-1 text-[11px] font-medium uppercase tracking-[0.12em] text-sky-700">
                About Your Company
              </span>

            </div>
            <h2 className="mt-3 font-display text-xl font-normal tracking-normal text-[#0b1f3a]">
              Give matching a clearer read on your company
            </h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">
              Complete the employer questionnaire so we can understand culture, support style, benefits, operating model, and candidate success traits alongside each role.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="mb-2 flex items-center justify-between gap-3 text-xs font-medium text-slate-500">
            <span>{answered} of {total} answered</span>
            <span className="text-[#0b1f3a]">{completion}%</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-white ring-1 ring-slate-200">
            <div className="h-full rounded-full bg-[#0ea4f9]" style={{ width: `${completion}%` }} />
          </div>
          <div className="mt-3 flex items-center justify-between gap-3">
            {/* <span className="text-xs text-slate-500">
              {remainingToTarget} more {remainingToTarget === 1 ? 'answer' : 'answers'} to reach 75%
            </span> */}
            <span className="inline-flex items-center gap-1 text-xs font-medium text-sky-700 transition group-hover:translate-x-0.5">
              Continue <PiCaretRightLight size={16} className="mt-0.5" />
            </span>
          </div>
        </div>
      </div>
    </button>
  );
};

const MetricCard = ({ icon: Icon, label, value, detail, trend, tone = 'blue', onClick, actionText }) => {
  const tones = {
    blue: 'text-sky-600',
    ink: 'text-[#0b1f3a]',
    cyan: 'text-cyan-600',
    green: 'text-emerald-600'
  };

  const actionTones = {
    blue: 'text-sky-600 group-hover:text-sky-700',
    ink: 'text-slate-600 group-hover:text-[#0b1f3a]',
    cyan: 'text-cyan-600 group-hover:text-cyan-700',
    green: 'text-emerald-600 group-hover:text-emerald-700'
  };

  const Component = onClick ? 'button' : 'div';
  const interactionClasses = onClick
    ? 'group block w-full text-left cursor-pointer transition-all hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_22px_54px_rgba(15,23,42,0.08)] focus:outline-none focus:ring-2 focus:ring-sky-500/50'
    : '';

  return (
    <Component
      onClick={onClick}
      className={`min-w-0 flex flex-col h-full rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.04)] ${interactionClasses}`}
    >
      <div className="mb-4 flex items-start justify-between gap-3">
        <span className={`flex shrink-0 items-center justify-center ${tones[tone] || tones.blue}`}>
          <Icon size={30} />
        </span>
        <span className="shrink-0 rounded-md bg-slate-50 px-2 py-1 text-[11px] font-medium text-slate-500">
          {getTrendCopy(trend)}
        </span>
      </div>

      <div className="mb-4 flex-1">
        <div className="truncate font-display text-2xl font-normal leading-none text-[#0b1f3a]">{value}</div>
        <div className="mt-2 truncate text-sm font-medium text-slate-700">{label}</div>
        <div className="mt-1 truncate text-xs leading-5 text-slate-500">{detail}</div>
      </div>

      {onClick && actionText && (
        <div className={`mt-auto flex items-center justify-between border-t border-slate-100 pt-3 text-xs font-medium transition-colors ${actionTones[tone] || actionTones.blue}`}>
          <span>{actionText}</span>
          <PiCaretRightLight size={16} className="transition-transform group-hover:translate-x-1" />
        </div>
      )}
    </Component>
  );
};

const Funnel = ({ data }) => {
  if (!data?.some((item) => item.count > 0)) return <EmptyState label="Pipeline funnel will appear after matches are created" />;

  return (
    <div className="space-y-3">
      {data.map((stage) => (
        <div key={stage.label}>
          <div className="mb-1.5 flex items-center justify-between gap-3">
            <span className="truncate text-xs font-medium text-slate-600">{stage.label}</span>
            <span className="text-xs font-medium text-[#0b1f3a]">{formatNumber(stage.count)}</span>
          </div>
          <div className="h-2 overflow-hidden rounded-full bg-slate-100">
            <div
              className="h-full rounded-full bg-[#0ea4f9]"
              style={{ width: `${Math.max(3, Math.min(stage.percentage, 100))}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const StatusBadge = ({ children }) => (
  <span className="inline-flex rounded-md bg-slate-100 px-2 py-1 text-[11px] font-medium capitalize text-slate-600">
    {labelize(children)}
  </span>
);

const SkeletonBlock = ({ className = '', style }) => (
  <div
    className={`relative overflow-hidden bg-slate-200/60 ${className}`}
    style={style}
  >
    <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent" />
  </div>
);

const DashboardSkeleton = () => (
  <div className="min-h-screen bg-white p-4 pt-16 sm:p-5 sm:pt-5 lg:p-7">
    <style>{`
      @keyframes shimmer {
        100% { transform: translateX(100%); }
      }
      .animate-shimmer {
        animation: shimmer 1.5s infinite;
      }
    `}</style>
    <div className="mx-auto max-w-[1500px] space-y-5">
      {/* Header Skeleton */}
      <div className="rounded-lg border border-slate-200/80 bg-white/95 p-4 shadow-[0_14px_36px_rgba(15,23,42,0.045)] sm:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div className="space-y-3">
            <SkeletonBlock className="h-3 w-32 rounded-md" />
            <SkeletonBlock className="h-8 w-64 rounded-md sm:w-80" />
            <SkeletonBlock className="h-4 w-40 rounded-md" />
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <SkeletonBlock className="h-10 w-full rounded-lg md:w-80" />
            <SkeletonBlock className="h-10 w-full rounded-lg md:w-32" />
            <SkeletonBlock className="hidden h-10 w-10 rounded-lg md:block" />
          </div>
        </div>
      </div>

      {/* Metrics Skeleton */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_12px_30px_rgba(15,23,42,0.04)]">
            <div className="mb-4 flex items-center justify-between">
              <SkeletonBlock className="h-9 w-9 rounded-lg" />
              <SkeletonBlock className="h-5 w-16 rounded-md" />
            </div>
            <SkeletonBlock className="mb-3 h-7 w-24 rounded-md" />
            <SkeletonBlock className="mb-2 h-3.5 w-32 rounded-md" />
            <SkeletonBlock className="h-3 w-48 rounded-md" />
          </div>
        ))}
      </div>

      {/* Main Content Skeleton */}
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
        <div className="space-y-5">
          <div className="grid gap-5 2xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
            {/* Main Chart */}
            <div className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_14px_36px_rgba(15,23,42,0.045)] sm:p-5">
              <div className="mb-6 flex justify-between">
                 <div className="space-y-2">
                    <SkeletonBlock className="h-5 w-48 max-w-full rounded-md" />
                    <SkeletonBlock className="h-3 w-64 max-w-full rounded-md" />
                 </div>
                 <SkeletonBlock className="hidden h-8 w-8 rounded-full sm:block" />
              </div>
              <div className="flex h-[340px] items-end gap-2 sm:gap-4">
                {[40, 70, 45, 90, 65, 85, 55, 75, 50, 80, 60, 95].map((height, i) => (
                  <SkeletonBlock key={i} className="w-full rounded-t-md" style={{ height: `${height}%` }} />
                ))}
              </div>
            </div>
            {/* Doughnut Chart */}
            <div className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_14px_36px_rgba(15,23,42,0.045)] sm:p-5">
              <div className="mb-6 space-y-2">
                <SkeletonBlock className="h-5 w-36 max-w-full rounded-md" />
                <SkeletonBlock className="h-3 w-52 max-w-full rounded-md" />
              </div>
              <div className="flex h-[320px] items-center justify-center">
                <SkeletonBlock className="h-56 w-56 rounded-full" />
              </div>
            </div>
          </div>

          {/* Two bottom panels */}
          <div className="grid gap-5 lg:grid-cols-2">
            {[...Array(2)].map((_, i) => (
              <div key={i} className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_14px_36px_rgba(15,23,42,0.045)] sm:p-5">
                <div className="mb-6 space-y-2">
                  <SkeletonBlock className="h-5 w-40 max-w-full rounded-md" />
                  <SkeletonBlock className="h-3 w-48 max-w-full rounded-md" />
                </div>
                <div className="flex h-[260px] flex-col justify-end gap-4">
                    {[70, 85, 60, 90, 50, 75].map((width, j) => (
                        <SkeletonBlock key={j} className="h-6 rounded-md" style={{ width: `${width}%` }} />
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar Skeleton */}
        <aside className="space-y-5">
          {/* Status Panel */}
          <div className="relative overflow-hidden rounded-lg bg-[#03070d] p-5 shadow-[0_18px_40px_rgba(3,7,13,0.18)]">
            <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/5 to-transparent" />
            <div className="relative z-10 mb-5 flex justify-between">
              <div className="space-y-2">
                <div className="h-6 w-32 rounded-md bg-white/10" />
                <div className="h-3 w-20 rounded-md bg-white/5" />
              </div>
              <div className="h-6 w-6 rounded-full bg-white/10" />
            </div>
            <div className="relative z-10 mb-6 space-y-2">
               <div className="flex justify-between">
                   <div className="h-3 w-24 rounded-md bg-white/10" />
                   <div className="h-3 w-8 rounded-md bg-white/10" />
               </div>
               <div className="h-2 w-full rounded-full bg-white/5" />
            </div>
            <div className="relative z-10 grid grid-cols-2 gap-3 border-t border-white/10 pt-4">
              <div className="space-y-2">
                <div className="h-8 w-16 rounded-md bg-white/10" />
                <div className="h-3 w-20 rounded-md bg-white/5" />
              </div>
              <div className="space-y-2">
                <div className="h-8 w-16 rounded-md bg-white/10" />
                <div className="h-3 w-20 rounded-md bg-white/5" />
              </div>
            </div>
            <div className="relative z-10 mt-5 h-10 w-full rounded-lg bg-white/10" />
          </div>

          {/* List Panels */}
          {[...Array(2)].map((_, i) => (
             <div key={i} className="rounded-lg border border-slate-200/80 bg-white p-4 shadow-[0_14px_36px_rgba(15,23,42,0.045)] sm:p-5">
               <div className="mb-6 space-y-2">
                 <SkeletonBlock className="h-5 w-32 max-w-full rounded-md" />
                 <SkeletonBlock className="h-3 w-48 max-w-full rounded-md" />
               </div>
               <div className="space-y-4">
                 {[...Array(4)].map((_, j) => (
                    <div key={j} className="flex gap-3">
                        <SkeletonBlock className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" />
                        <div className="flex-1 space-y-2 pb-2">
                            <SkeletonBlock className="h-4 w-3/4 max-w-full rounded-md" />
                            <SkeletonBlock className="h-3 w-1/2 max-w-full rounded-md" />
                            <SkeletonBlock className="h-2 w-1/3 max-w-full rounded-md" />
                        </div>
                    </div>
                 ))}
               </div>
             </div>
          ))}
        </aside>
      </div>
    </div>
  </div>
);

const DashboardOverview = () => {
  const navigate = useNavigate();
  const { openCommandPalette } = useUIStore();
  const [timeframe, setTimeframe] = useState('90d');
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [notificationsError, setNotificationsError] = useState('');
  const [notificationActionId, setNotificationActionId] = useState(null);
  const [markingAllRead, setMarkingAllRead] = useState(false);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [highlightCompanyKnowledge, setHighlightCompanyKnowledge] = useState(false);
  const notificationMenuRef = useRef(null);
  const companyKnowledgeRef = useRef(null);

  const {
    data,
    error,
    isLoading,
    isFetching,
    refetch
  } = useQuery(['dashboard-overview', timeframe], () => dashboardAPI.getOverview(timeframe), {
    keepPreviousData: true,
    staleTime: 60 * 1000
  });

  const {
    data: profileSetupData,
    isLoading: isProfileSetupLoading
  } = useQuery(['employer-profile-setup'], () => employerAPI.getProfileSetup(), {
    staleTime: 5 * 60 * 1000,
    retry: false
  });

  const dashboard = data?.dashboard || data?.data || data;
  const profileSetup = profileSetupData?.data || profileSetupData;
  const companyQuestionProgress = profileSetup?.questionProgress || {};

  const summary = dashboard?.summary;
  const charts = dashboard?.charts || {};
  const lists = dashboard?.lists || {};
  const employer = dashboard?.employer;
  const contact = employer?.contact || {};
  const firstName = contact.firstName || 'there';
  const totalCompanyQuestions = Number(companyQuestionProgress.totalQuestions || 0);
  const answeredCompanyQuestions = Number(companyQuestionProgress.answeredQuestions || 0);
  const shouldShowKnowledgeBanner = (
    !isProfileSetupLoading &&
    totalCompanyQuestions > 0 &&
    Number(companyQuestionProgress.completionPercentage || 0) < 75
  );

  const filteredMatches = lists.recentMatches || [];

  const loadNotifications = useCallback(async () => {
    setNotificationsLoading(true);
    setNotificationsError('');

    try {
      const response = await dashboardAPI.getNotifications({ limit: 8, offset: 0 });
      const payload = response?.data || response;
      setNotifications(Array.isArray(payload?.notifications) ? payload.notifications : []);
      setUnreadNotificationCount(Number(payload?.pagination?.unreadCount || 0));
    } catch (_error) {
      setNotificationsError('Notifications could not be loaded. Try again.');
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  useEffect(() => {
    setUnreadNotificationCount(Number(summary?.unreadNotifications || 0));
  }, [summary?.unreadNotifications]);

  useEffect(() => {
    if (!notificationsOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!notificationMenuRef.current?.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setNotificationsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [notificationsOpen]);

  useEffect(() => {
    if (
      !dashboard ||
      isProfileSetupLoading ||
      !shouldShowKnowledgeBanner ||
      answeredCompanyQuestions !== 0
    ) {
      return undefined;
    }

    const employerKey = employer?.id || contact.email;
    if (!employerKey) return undefined;

    const storageKey = `sanspath.employer.dashboard.companyKnowledgeCue.${employerKey}`;
    try {
      if (window.sessionStorage.getItem(storageKey) === '1') return undefined;
    } catch (_error) {
      // The cue can still run when browser storage is unavailable.
    }

    setHighlightCompanyKnowledge(true);
    const scrollTimer = window.setTimeout(() => {
      companyKnowledgeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    const releaseTimer = window.setTimeout(() => {
      setHighlightCompanyKnowledge(false);
      try {
        window.sessionStorage.setItem(storageKey, '1');
      } catch (_error) {
        // Storage availability should not affect the dashboard.
      }
    }, 2100);

    return () => {
      window.clearTimeout(scrollTimer);
      window.clearTimeout(releaseTimer);
    };
  }, [
    answeredCompanyQuestions,
    contact.email,
    dashboard,
    employer?.id,
    isProfileSetupLoading,
    shouldShowKnowledgeBanner
  ]);

  const toggleNotifications = () => {
    if (notificationsOpen) {
      setNotificationsOpen(false);
      return;
    }

    setNotificationsOpen(true);
    loadNotifications();
  };

  const openNotification = async (notification) => {
    const destination = getNotificationDestination(notification);

    if (!notification.read) {
      setNotificationActionId(notification.id);
      setNotificationsError('');
      try {
        await dashboardAPI.markNotificationRead(notification.id);
        setNotifications((current) => current.map((item) => (
          item.id === notification.id ? { ...item, read: true } : item
        )));
        setUnreadNotificationCount((current) => Math.max(0, current - 1));
      } catch (_error) {
        setNotificationsError('This notification could not be updated. Try again.');
        setNotificationActionId(null);
        return;
      }
      setNotificationActionId(null);
    }

    if (destination) {
      setNotificationsOpen(false);
      navigate(destination);
    }
  };

  const markAllNotificationsRead = async () => {
    if (markingAllRead || unreadNotificationCount === 0) return;

    setMarkingAllRead(true);
    setNotificationsError('');
    try {
      await dashboardAPI.markAllNotificationsRead();
      setNotifications((current) => current.map((item) => ({ ...item, read: true })));
      setUnreadNotificationCount(0);
    } catch (_error) {
      setNotificationsError('Notifications could not be marked as read. Try again.');
    } finally {
      setMarkingAllRead(false);
    }
  };

  const activityData = useMemo(() => {
    const series = charts.timeSeries || [];
    return {
      labels: series.map((item) => item.label),
      datasets: [
        {
          type: 'bar',
          label: 'Matches',
          data: series.map((item) => item.matches),
          backgroundColor: palette.ink,
          borderRadius: 4,
          barThickness: 18,
          maxBarThickness: 22
        },
        {
          type: 'line',
          label: 'Interviews',
          data: series.map((item) => item.interviews),
          borderColor: palette.sky,
          backgroundColor: 'rgba(14,164,249,0.12)',
          pointRadius: 2,
          pointHoverRadius: 4,
          tension: 0.35,
          fill: true
        },
        {
          type: 'line',
          label: 'Offers',
          data: series.map((item) => item.offers),
          borderColor: palette.cyan,
          backgroundColor: 'rgba(35,203,252,0.08)',
          pointRadius: 2,
          pointHoverRadius: 4,
          tension: 0.35
        }
      ]
    };
  }, [charts.timeSeries]);

  const jobFlowData = useMemo(() => {
    const series = charts.timeSeries || [];
    return {
      labels: series.map((item) => item.label),
      datasets: [
        {
          label: 'Jobs created',
          data: series.map((item) => item.jobs),
          borderColor: palette.blue,
          backgroundColor: 'rgba(2,111,235,0.12)',
          pointRadius: 2,
          tension: 0.35,
          fill: true
        }
      ]
    };
  }, [charts.timeSeries]);

  const matchBandData = useMemo(() => ({
    labels: (charts.matchBands || []).map((item) => item.label),
    datasets: [
      {
        data: (charts.matchBands || []).map((item) => item.value),
        backgroundColor: [palette.blue, palette.sky, palette.cyan, palette.pale, palette.slate],
        borderWidth: 0,
        hoverOffset: 4
      }
    ]
  }), [charts.matchBands]);

  const locationData = useMemo(() => ({
    labels: (charts.candidateLocations || []).map((item) => item.label),
    datasets: [
      {
        label: 'Candidates',
        data: (charts.candidateLocations || []).map((item) => item.value),
        backgroundColor: 'rgba(14,164,249,0.82)',
        borderRadius: 4,
        barThickness: 16
      }
    ]
  }), [charts.candidateLocations]);

  const skillGapData = useMemo(() => ({
    labels: (charts.skillGaps || []).map((item) => item.skill),
    datasets: [
      {
        label: 'Missing count',
        data: (charts.skillGaps || []).map((item) => item.count),
        backgroundColor: palette.ink,
        borderRadius: 4,
        barThickness: 14
      }
    ]
  }), [charts.skillGaps]);

  if (isLoading) return <DashboardSkeleton />;

  if (error || !dashboard) {
    return (
      <div className="flex min-h-screen items-center justify-center p-5">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 text-center shadow-[0_14px_36px_rgba(15,23,42,0.06)]">
          <PiWarningCircleLight className="mx-auto mb-3 text-sky-700" size={26} />
          <h1 className="font-display text-xl font-normal text-[#0b1f3a]">Dashboard data is unavailable</h1>
          <p className="mt-2 text-sm leading-6 text-slate-500">
            The employer analytics endpoint did not return data. Try again after confirming the API is running.
          </p>
          <button
            onClick={() => refetch()}
            className="mt-5 inline-flex h-10 items-center justify-center rounded-lg bg-[#0b1f3a] px-4 text-sm font-medium text-white transition hover:bg-[#12345f]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const metricCards = [
    {
      label: 'Active jobs',
      value: formatNumber(summary.activeJobs),
      detail: `${formatNumber(summary.openPositions)} open seats across ${formatNumber(summary.totalJobs)} roles`,
      trend: dashboard.trends?.jobs,
      icon: PiBriefcaseLight,
      tone: 'blue',
      onClick: () => navigate('/jobs?status=active'),
      actionText: 'View jobs'
    },
    {
      label: 'Talent matches',
      value: formatNumber(summary.totalMatches),
      detail: `${formatNumber(summary.highMatches)} high-confidence matches`,
      trend: dashboard.trends?.matches,
      icon: PiUsersLight,
      tone: 'ink',
      onClick: () => navigate('/candidates'),
      actionText: 'View matches'
    },
    {
      label: 'Average match score',
      value: formatPercent(summary.averageMatchScore),
      detail: `${formatPercent(summary.mutualRate)} mutual-interest conversion`,
      trend: dashboard.trends?.matches,
      icon: PiTargetLight,
      tone: 'cyan',
      onClick: () => navigate('/candidates/bands'),
      actionText: 'View bands'
    },
    {
      label: 'Offer acceptance',
      value: formatPercent(summary.offerAcceptanceRate),
      detail: `${formatMoney(summary.averageOfferAmount)} average offer value`,
      trend: dashboard.trends?.offers,
      icon: PiSignatureLight,
      tone: 'green',
      onClick: () => navigate('/offers'),
      actionText: 'View offers'
    }
  ];

  const profileStrength = Math.max(0, Math.min(100, Number(companyQuestionProgress?.completionPercentage) || 25)); // Default to 25% if no data
  const readinessLabel = profileStrength >= 80 ? 'Highly optimized' : profileStrength >= 50 ? 'Ready to match' : 'Needs optimization';

  const hasMatchBands = charts.matchBands?.some((item) => item.value > 0);
  const hasLocations = charts.candidateLocations?.some((item) => item.value > 0);
  const hasSkillGaps = charts.skillGaps?.some((item) => item.count > 0);
  return (
    <div className="min-h-screen bg-white p-4 pt-16 sm:p-5 sm:pt-5 lg:p-7">
      <div
        className={`pointer-events-none fixed inset-0 z-[60] bg-slate-900/10 backdrop-blur-[1px] transition-opacity duration-700 ${
          highlightCompanyKnowledge ? 'opacity-100' : 'opacity-0'
        }`}
        aria-hidden="true"
      />
      <div className="mx-auto max-w-[1500px] space-y-5">
        <header className="rounded-lg border border-slate-200/80 bg-white/95 p-4 shadow-[0_14px_36px_rgba(15,23,42,0.045)] backdrop-blur sm:p-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-sky-700">
                {employer.companyName || 'Employer workspace'}
              </p>
              <h1 className="mt-1 font-display text-2xl font-normal tracking-normal text-[#0b1f3a] sm:text-3xl">
                {getGreeting()}, {firstName}
              </h1>
              <p className="mt-1 text-sm text-slate-500">
                {dashboard.generatedAt ? `Updated ${relativeTime(dashboard.generatedAt)}` : 'Live employer analytics'}
              </p>
            </div>

            <div className="flex flex-col gap-3 md:flex-row md:items-center">
              {/* ⌘K palette trigger — replaces the false-affordance search input */}
              <button
                id="cmd-palette-trigger"
                onClick={openCommandPalette}
                className="group flex h-10 items-center gap-2.5 rounded-lg border border-slate-200 bg-slate-50 pl-3 pr-2.5 text-sm text-slate-400 transition hover:border-sky-300 hover:bg-white hover:text-slate-500 md:w-72"
                aria-label="Open command palette"
              >
                <PiMagnifyingGlassLight size={18} className="shrink-0" />
                <span className="hidden flex-1 text-left md:block">Search jobs, candidates…</span>
                <span className="hidden items-center gap-0.5 md:flex">
                  <kbd className="flex h-5 items-center rounded border border-slate-200 bg-white px-1.5 font-mono text-[10px] font-medium text-slate-500 shadow-[0_1px_0_rgba(0,0,0,0.08)]">
                    <PiCommandLight size={12} className="mr-0.5" />K
                  </kbd>
                </span>
              </button>

              <div className="flex items-center gap-3">

                <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
                  {timeframeOptions.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setTimeframe(option.value)}
                      className={`h-8 rounded-md px-3 text-xs font-medium transition ${
                        timeframe === option.value
                          ? 'bg-white text-[#0b1f3a] shadow-[0_4px_12px_rgba(15,23,42,0.06)]'
                          : 'text-slate-500 hover:text-slate-900'
                      }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative" ref={notificationMenuRef}>
                <button
                  type="button"
                  onClick={toggleNotifications}
                  className={`relative flex h-10 w-10 items-center justify-center rounded-lg border bg-white transition ${
                    notificationsOpen
                      ? 'border-sky-300 text-sky-700 ring-2 ring-sky-100'
                      : 'border-slate-200 text-slate-500 hover:border-sky-200 hover:text-sky-700'
                  }`}
                  aria-label="Open notifications"
                  aria-haspopup="dialog"
                  aria-expanded={notificationsOpen}
                >
                  <PiBellLight size={20} />
                  {unreadNotificationCount > 0 && (
                    <span className="absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-sky-600 px-1 text-[9px] font-semibold leading-none text-white ring-2 ring-white">
                      {unreadNotificationCount > 9 ? '9+' : unreadNotificationCount}
                    </span>
                  )}
                </button>

                {notificationsOpen && (
                  <div
                    role="dialog"
                    aria-label="Notifications"
                    className="absolute right-0 top-12 z-50 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_22px_60px_rgba(15,23,42,0.16)]"
                  >
                    <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-4 py-3">
                      <div>
                        <h2 className="text-sm font-semibold text-[#0b1f3a]">Notifications</h2>
                        <p className="mt-0.5 text-xs text-slate-500">
                          {unreadNotificationCount > 0 ? `${unreadNotificationCount} unread` : 'You are up to date'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={markAllNotificationsRead}
                        disabled={markingAllRead || unreadNotificationCount === 0}
                        className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-sky-700 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:text-slate-300"
                      >
                        {markingAllRead
                          ? <PiCircleNotchLight size={15} className="animate-spin" />
                          : <PiCheckCircleLight size={15} />}
                        Mark all read
                      </button>
                    </div>

                    {notificationsError && (
                      <div className="flex items-start gap-2 border-b border-rose-100 bg-rose-50 px-4 py-2.5 text-xs leading-5 text-rose-700">
                        <PiWarningCircleLight size={16} className="mt-0.5 shrink-0" />
                        <span className="flex-1">{notificationsError}</span>
                        <button type="button" onClick={loadNotifications} className="font-semibold hover:underline">
                          Retry
                        </button>
                      </div>
                    )}

                    <div className="max-h-[min(26rem,60vh)] overflow-y-auto custom-scrollbar">
                      {notificationsLoading ? (
                        <div className="flex items-center justify-center gap-2 px-4 py-10 text-sm text-slate-500">
                          <PiCircleNotchLight size={18} className="animate-spin text-sky-600" />
                          Loading notifications
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="px-6 py-10 text-center">
                          <PiBellLight size={24} className="mx-auto text-slate-300" />
                          <p className="mt-3 text-sm font-medium text-[#0b1f3a]">No notifications yet</p>
                          <p className="mt-1 text-xs leading-5 text-slate-500">Hiring updates will appear here.</p>
                        </div>
                      ) : notifications.map((notification) => (
                        <button
                          key={notification.id}
                          type="button"
                          onClick={() => openNotification(notification)}
                          disabled={notificationActionId === notification.id}
                          className={`group flex w-full gap-3 border-b border-slate-100 px-4 py-3 text-left transition last:border-b-0 hover:bg-slate-50 disabled:cursor-wait ${
                            notification.read ? 'bg-white' : 'bg-sky-50/55'
                          }`}
                        >
                          <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${notification.read ? 'bg-slate-200' : 'bg-sky-500'}`} />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-start justify-between gap-3">
                              <span className={`text-sm leading-5 ${notification.read ? 'font-medium text-slate-700' : 'font-semibold text-[#0b1f3a]'}`}>
                                {notification.title || labelize(notification.type)}
                              </span>
                              <span className="shrink-0 text-[11px] text-slate-400">{relativeTime(notification.createdAt)}</span>
                            </span>
                            {notification.message && (
                              <span className="mt-1 block text-xs leading-5 text-slate-500">{notification.message}</span>
                            )}
                          </span>
                          {notificationActionId === notification.id && (
                            <PiCircleNotchLight size={15} className="mt-1 shrink-0 animate-spin text-sky-600" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {shouldShowKnowledgeBanner && (
          <CompanyKnowledgeBanner
            progress={companyQuestionProgress}
            onClick={() => navigate('/profile?tab=culture&focus=company-questions')}
            highlighted={highlightCompanyKnowledge}
            bannerRef={companyKnowledgeRef}
          />
        )}

        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          {metricCards.map((metric) => <MetricCard key={metric.label} {...metric} />)}
        </div>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="min-w-0 space-y-5">
            <div className="grid gap-5 2xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.65fr)]">
              <Panel
                icon={PiChartBarLight}
                title="Talent Activity"
                subtitle="Matches, interviews, and offers generated by the employer workflow."
                action={isFetching ? <PiCircleNotchLight size={16} className="animate-spin text-sky-600" /> : null}
              >
                {(charts.timeSeries || []).length > 0 ? (
                  <div className="relative h-[340px] w-full">
                    <Bar data={activityData} options={chartOptions} />
                  </div>
                ) : (
                  <EmptyState label="Monthly activity will appear after jobs receive matches" />
                )}
              </Panel>

              <Panel
                icon={PiTargetLight}
                title="Match Quality"
                subtitle="Distribution by backend match band."
              >
                {hasMatchBands ? (
                  <div className="relative h-[320px] w-full">
                    <Doughnut data={matchBandData} options={doughnutOptions} />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="font-display text-3xl font-normal text-[#0b1f3a]">
                          {formatPercent(summary.averageMatchScore)}
                        </div>
                        <div className="mt-1 text-[11px] font-medium uppercase tracking-[0.14em] text-slate-400">
                          Avg score
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <EmptyState label="Match bands will appear after matching runs" />
                )}
              </Panel>
            </div>

            <div className="grid gap-5 lg:grid-cols-2">
              <Panel icon={PiChartLineUpLight} title="Job Creation Trend" subtitle="New roles created from backend job postings.">
                <div className="relative h-[260px] w-full">
                  <Line data={jobFlowData} options={chartOptions} />
                </div>
              </Panel>

              <Panel icon={PiFunnelLight} title="Candidate Locations" subtitle="Where matched candidates are concentrated.">
                {hasLocations ? (
                  <div className="relative h-[260px] w-full">
                    <Bar data={locationData} options={{ ...chartOptions, indexAxis: 'y' }} />
                  </div>
                ) : (
                  <EmptyState label="Locations will appear when matched candidates have profile locations" />
                )}
              </Panel>
            </div>

            <div className="grid gap-5 lg:grid-cols-[0.82fr_1.18fr]">
              <Panel icon={PiPulseLight} title="Conversion Funnel" subtitle="The end-to-end employer hiring pipeline.">
                <Funnel data={charts.funnel || []} />
              </Panel>

              <Panel icon={PiWarningCircleLight} title="Skill Gap Pressure" subtitle="Most repeated missing skills across matches.">
                {hasSkillGaps ? (
                  <div className="relative h-[300px] w-full">
                    <Bar data={skillGapData} options={{ ...chartOptions, indexAxis: 'y' }} />
                  </div>
                ) : (
                  <EmptyState label="Skill gaps will appear when matches include missing-skill signals" />
                )}
              </Panel>
            </div>

            <Panel
              icon={PiBriefcaseLight}
              title="Top Performing Jobs"
              subtitle="Roles ranked by match volume and average match quality."
              action={
                <button
                  onClick={() => navigate('/jobs')}
                  className="hidden h-8 items-center gap-1 rounded-lg border border-slate-200 px-3 text-xs font-medium text-slate-600 transition hover:border-sky-200 hover:text-sky-700 sm:flex"
                >
                  View jobs <PiCaretRightLight size={16} />
                </button>
              }
            >
              {(lists.topJobs || []).length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[680px] border-collapse text-left">
                    <thead>
                      <tr className="border-b border-slate-100 text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
                        <th className="pb-3">Role</th>
                        <th className="pb-3">Status</th>
                        <th className="pb-3 text-right">Matches</th>
                        <th className="pb-3 text-right">Avg Score</th>
                        <th className="pb-3 text-right">Interviews</th>
                        <th className="pb-3 text-right">Offers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lists.topJobs.map((job) => (
                        <tr key={job.id} className="border-b border-slate-50 last:border-0">
                          <td className="py-3 pr-4">
                            <button
                              onClick={() => navigate(`/jobs/${job.id}`)}
                              className="max-w-[260px] truncate text-sm font-medium text-[#0b1f3a] transition hover:text-sky-700"
                            >
                              {job.title}
                            </button>
                            <div className="mt-1 h-1.5 max-w-[220px] overflow-hidden rounded-full bg-slate-100">
                              <div
                                className="h-full rounded-full bg-[#0ea4f9]"
                                style={{ width: `${Math.min(100, job.averageMatchScore)}%` }}
                              />
                            </div>
                          </td>
                          <td className="py-3"><StatusBadge>{job.status}</StatusBadge></td>
                          <td className="py-3 text-right text-sm text-slate-700">{formatNumber(job.totalMatches)}</td>
                          <td className="py-3 text-right text-sm text-slate-700">{formatPercent(job.averageMatchScore)}</td>
                          <td className="py-3 text-right text-sm text-slate-700">{formatNumber(job.interviews)}</td>
                          <td className="py-3 text-right text-sm text-slate-700">{formatNumber(job.offers)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <EmptyState label="Create jobs to populate performance rankings" />
              )}
            </Panel>

            <Panel icon={PiUsersLight} title="Recent Matches" subtitle="Latest AI matches with score and band (live feed).">
              {filteredMatches.length > 0 ? (
                <div className="grid gap-3 lg:grid-cols-2">
                  {filteredMatches.map((match) => {
                    const skills = (match.matchingSkills || []).map(normalizeSkill).filter(Boolean).slice(0, 3);
                    return (
                      <button
                        key={match.id}
                        onClick={() => navigate(`/jobs/${match.job.id}`)}
                        className="rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-left transition hover:border-sky-200 hover:bg-white"
                      >
                        <div className="mb-3 flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-[#0b1f3a]">{match.candidate.name}</div>
                            <div className="mt-1 truncate text-xs text-slate-500">{match.job.title}</div>
                          </div>
                          <span className="rounded-md bg-white px-2 py-1 text-xs font-medium text-sky-700 ring-1 ring-sky-100">
                            {formatPercent(match.score)}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {skills.length > 0 ? skills.map((skill) => (
                            <span key={skill} className="rounded-md bg-white px-2 py-1 text-[11px] font-medium text-slate-600 ring-1 ring-slate-200">
                              {skill}
                            </span>
                          )) : (
                            <span className="text-xs text-slate-400">No matched skills listed</span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <EmptyState label="Recent matches will appear after matching runs" />
              )}
            </Panel>
          </div>

          <aside className="min-w-0 space-y-5">
            <section className="min-w-0 rounded-lg bg-[#03070d] p-5 text-white shadow-[0_18px_40px_rgba(3,7,13,0.18)]">
              <div className="mb-5 flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-lg font-normal">Matching Readiness</h2>
                  <p className="mt-1 text-xs font-medium text-slate-400">
                    {readinessLabel}
                  </p>
                </div>
                <PiCheckCircleLight className={profileStrength >= 80 ? 'text-[#66d8fc]' : 'text-slate-500'} size={24} />
              </div>

              <div className="mb-5">
                <div className="mb-2 flex items-center justify-between text-xs text-slate-300">
                  <span>Profile strength</span>
                  <span>{profileStrength}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-white/15">
                  <div className="h-full rounded-full bg-[#66d8fc] transition-all duration-1000 ease-out" style={{ width: `${profileStrength}%` }} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 border-t border-white/10 pt-4">
                <div>
                  <div className="text-2xl font-normal">{formatNumber(summary.openPositions)}</div>
                  <div className="mt-1 text-[11px] text-slate-400">Open seats</div>
                </div>
                <div>
                  <div className="text-2xl font-normal">{formatPercent(summary.fillRate)}</div>
                  <div className="mt-1 text-[11px] text-slate-400">Fill rate</div>
                </div>
              </div>

              <button
                onClick={() => navigate('/config')}
                className="mt-5 flex h-10 w-full items-center justify-center rounded-lg bg-white text-sm font-medium text-[#0b1f3a] transition hover:bg-[#e9f9ff]"
              >
                View setup
              </button>
            </section>

            <Panel icon={PiLightbulbLight} title="Smart Insights" subtitle="Actionable recommendations based on your hiring data.">
              {(dashboard.insights || []).length > 0 ? (
                <div className="space-y-3">
                  {dashboard.insights.map((insight) => {
                    const action = (() => {
                      const l = String(insight.label || '').toLowerCase();
                      if (l.includes('job') || l.includes('fill') || l.includes('position')) return { text: 'View jobs', route: '/jobs' };
                      if (l.includes('offer') || l.includes('accept')) return { text: 'View offers', route: '/offers' };
                      if (l.includes('interview')) return { text: 'View interviews', route: '/interviews' };
                      return { text: 'Review candidates', route: '/candidates' };
                    })();

                    return (
                      <button
                        key={insight.id}
                        onClick={() => navigate(action.route)}
                        className="group block w-full text-left rounded-lg border border-slate-200 bg-white p-3 transition-all hover:-translate-y-0.5 hover:border-sky-200 hover:shadow-[0_8px_16px_rgba(14,164,249,0.06)]"
                      >
                        <div className="mb-1 flex items-center justify-between gap-3">
                          <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-sky-700">
                            {labelize(insight.label)}
                          </span>
                          <PiArrowUpRightLight size={16} className={`transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${insight.tone === 'positive' ? 'text-emerald-600' : 'text-sky-500'}`} />
                        </div>
                        <div className="truncate text-sm font-medium text-[#0b1f3a]">{insight.value}</div>
                        <p className="mt-1 text-xs leading-5 text-slate-500 line-clamp-2 whitespace-normal">{insight.detail}</p>
                        <div className="mt-2 flex items-center gap-1 text-[11px] font-medium text-sky-600 opacity-0 transition-opacity group-hover:opacity-100">
                          {action.text} <PiCaretRightLight size={14} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <EmptyState label="Insights will appear as jobs, matches, and offers accumulate" />
              )}
            </Panel>

            <Panel icon={PiCalendarCheckLight} title="Upcoming Interviews" subtitle={`${formatNumber(summary.upcomingInterviews)} scheduled ahead.`}>
              {(lists.upcomingInterviews || []).length > 0 ? (
                <div className="space-y-3">
                  {lists.upcomingInterviews.map((interview) => (
                    <div key={interview.id} className="rounded-lg border border-slate-200 bg-white p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-[#0b1f3a]">{interview.candidateName}</div>
                          <div className="mt-1 truncate text-xs text-slate-500">{interview.jobTitle}</div>
                        </div>
                        <span className="rounded-md bg-[#eef9ff] px-2 py-1 text-[11px] font-medium text-sky-700">
                          {formatPercent(interview.score)}
                        </span>
                      </div>
                      <div className="mt-3 text-xs font-medium text-slate-600">{formatDateTime(interview.scheduledDate)}</div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState label="No upcoming interviews from the backend" />
              )}
            </Panel>

            <Panel icon={PiPulseLight} title="Recent Activity" subtitle="Latest matches, jobs, and offers (live feed).">
              {(lists.recentActivity || []).length > 0 ? (
                <div className="space-y-3">
                  {lists.recentActivity.slice(0, 8).map((item) => (
                    <div key={`${item.type}-${item.id}`} className="flex gap-3">
                      <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-[#0ea4f9] ring-4 ring-sky-50" />
                      <div className="min-w-0 flex-1 border-b border-slate-100 pb-3">
                        <div className="truncate text-sm font-medium text-[#0b1f3a]">{item.primaryLabel}</div>
                        <div className="mt-0.5 truncate text-xs text-slate-500">{labelize(item.type)} · {item.secondaryLabel}</div>
                        <div className="mt-1 text-[11px] text-slate-400">{relativeTime(item.happenedAt)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState label="Backend activity will appear here" />
              )}
            </Panel>
          </aside>
        </div>
      </div>
      <DashboardGuideModal />
    </div>
  );
};

export default DashboardOverview;
